import React, { useState, useCallback, useMemo, useEffect } from "react";
import { Modal, Checkbox, Select, InputNumber, Button, Typography, Tooltip, Empty, Space } from "antd";
import {
  DownloadOutlined,
  InfoCircleOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  LoadingOutlined,
  BarChartOutlined,
  PieChartOutlined,
  LineChartOutlined,
  RadarChartOutlined,
  DotChartOutlined,
  PaperClipOutlined,
} from "@ant-design/icons";
import type {
  AprChart,
  AprChartOption,
  AprChartType,
  AprOptionValue,
  ChartOptionState,
  AllOptionsState,
  ChartDownloadResult,
  ChartDownloadModalProps,
} from "dtos/apr-chart.dto";
import { api } from "api";
import { TbImageInPicture } from "react-icons/tb";

const { Text } = Typography;

const CHART_TYPE_META: Record<AprChartType, { color: string; bg: string; label: string; icon: React.ReactNode }> = {
  pie: { color: "#e85d87", bg: "#fff0f5", label: "Pie", icon: <PieChartOutlined /> },
  bar: { color: "#3b7dd8", bg: "#eff6ff", label: "Bar", icon: <BarChartOutlined /> },
  line: { color: "#0e9e89", bg: "#f0fdfa", label: "Line", icon: <LineChartOutlined /> },
  radar: { color: "#d97706", bg: "#fffbeb", label: "Radar", icon: <RadarChartOutlined /> },
  mixed: { color: "#7c3aed", bg: "#f5f3ff", label: "Mixed", icon: <DotChartOutlined /> },
  canvas: { color: "#7c3aed", bg: "#f5f3ff", label: "Canvas", icon: <TbImageInPicture /> },
};

const ACCENT = "#6366f1";
const DARK_BG = "#f9fafb";
const DARK_BORDER = "#e5e7eb";
const DARK_TEXT = "#18181B";
const DARK_MUTED = "#71717A";

function parseConditional(description?: string): { key: string; value: string } | null {
  if (!description) return null;
  const m = description.match(/Required when (\w+)=(\w+)/i);
  return m ? { key: m[1], value: m[2] } : null;
}

function isOptionVisible(option: AprChartOption, state: ChartOptionState): boolean {
  const cond = parseConditional(option.description);
  if (!cond) return true;
  return state[cond.key] === cond.value;
}

function buildQueryString(chart: AprChart, state: ChartOptionState): string {
  const params = new URLSearchParams();
  for (const opt of chart.options) {
    if (!isOptionVisible(opt, state)) continue;
    const val = state[opt.name];
    if (val === undefined || val === null) continue;
    if (opt.type === "string[]" && Array.isArray(val) && val.length > 0) {
      params.set(opt.name, val.join(","));
    } else if (opt.type === "number" && typeof val === "number") {
      params.set(opt.name, String(val));
    } else if (typeof val === "string" && val.trim()) {
      params.set(opt.name, val.trim());
    }
  }
  const qs = params.toString();
  return qs ? `?${qs}` : "";
}

function defaultOptionsFor(chart: AprChart): ChartOptionState {
  const state: ChartOptionState = {};
  for (const opt of chart.options) {
    if (opt.default !== undefined) {
      state[opt.name] = opt.default;
    }
  }
  return state;
}

function truncate(str: string, max: number): string {
  return str.length > max ? `${str.slice(0, max)}…` : str;
}

function ChartTypeBadge({ type }: { type: AprChartType }) {
  const meta = CHART_TYPE_META[type];
  return (
    <span
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-semibold tracking-wide whitespace-nowrap flex-shrink-0"
      style={{ color: meta.color, background: meta.bg, border: `1px solid ${meta.color}33` }}
    >
      {meta.icon}
      {meta.label}
    </span>
  );
}

function ChartListItem({
  chart,
  selected,
  onToggle,
}: {
  chart: AprChart;
  selected: boolean;
  onToggle: (checked: boolean) => void;
}) {
  const disabled = !chart.available;
  const isActive = selected && !disabled;

  return (
    <div
      role="button"
      tabIndex={disabled ? -1 : 0}
      aria-disabled={disabled}
      aria-checked={isActive}
      onClick={() => !disabled && onToggle(!selected)}
      onKeyDown={(e) => !disabled && e.key === "Enter" && onToggle(!selected)}
      className="flex items-start gap-2.5 px-3.5 py-3 transition-colors duration-[120ms] select-none outline-none"
      style={{
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.36 : 1,
        // borderLeft: `3px solid ${isActive ? ACCENT : "transparent"}`,
        background: isActive ? "rgba(99,102,241,0.11)" : "transparent",
      }}
    >
      <Checkbox
        checked={isActive}
        disabled={disabled}
        onChange={(e) => onToggle(e.target.checked)}
        onClick={(e) => e.stopPropagation()}
        className="mt-[2px] flex-shrink-0"
      />

      <div className="flex-1 min-w-0">
        {/* Title row */}
        <div className="flex items-center justify-between gap-2 mb-1">
          <Text
            className="!text-[13px] !font-medium flex-1 min-w-0"
            style={{ color: DARK_TEXT }}
            ellipsis={{ tooltip: chart.title }}
          >
            {chart.title}
          </Text>
          <ChartTypeBadge type={chart.chartType} />
        </div>

        {/* Description */}
        <Text className="!text-[11px] !leading-4 block" style={{ color: DARK_MUTED }}>
          {truncate(chart.description, 82)}
        </Text>

        {/* Unavailable pill */}
        {disabled && (
          <span
            className="inline-block mt-1.5 px-1.5 py-px rounded-sm text-[10px] font-bold uppercase tracking-widest"
            style={{ color: "#6b7280", background: "#f4f4f5" }}
          >
            Unavailable
          </span>
        )}
      </div>
    </div>
  );
}

function OptionControl({
  option,
  value,
  siblingState,
  onChange,
}: {
  option: AprChartOption;
  value: AprOptionValue;

  siblingState: ChartOptionState;
  onChange: (v: AprOptionValue) => void;
}) {
  if (!isOptionVisible(option, siblingState)) return null;

  const humanLabel = option.name
    .replace(/([A-Z])/g, " $1")
    .replace(/_/g, " ")
    .toLowerCase();

  const labelNode = (
    <div className="flex items-center gap-1.5 mb-[5px]">
      <Text className="!text-[12px] !font-medium capitalize" style={{ color: "#374151" }}>
        {humanLabel}
      </Text>
      {option.description && (
        <Tooltip title={option.description} placement="topLeft" overlayStyle={{ maxWidth: 240 }}>
          <InfoCircleOutlined className="text-[11px] cursor-help" style={{ color: "#9ca3af" }} />
        </Tooltip>
      )}
    </div>
  );

  if (option.type === "string[]") {
    return (
      <div>
        {labelNode}
        <Select
          mode="multiple"
          value={Array.isArray(value) ? value : []}
          onChange={(val) => onChange(val as string[])}
          options={option.values?.map((v) => ({ value: v.value, label: v.label })) ?? []}
          className="w-full"
          placeholder="Select one or more…"
          size="small"
          allowClear
          maxTagCount="responsive"
        />
      </div>
    );
  }

  // Number input
  if (option.type === "number") {
    // Parse range hint from description e.g. "between 1 and 19"
    const rangeMatch = option.description?.match(/between (\d+) and (\d+)/i);
    const [min, max] = rangeMatch ? [Number(rangeMatch[1]), Number(rangeMatch[2])] : [undefined, undefined];

    return (
      <div>
        {labelNode}
        <InputNumber
          value={typeof value === "number" ? value : undefined}
          onChange={(v) => onChange(v ?? undefined)}
          min={min}
          max={max}
          className="w-full"
          size="small"
          placeholder={min !== undefined && max !== undefined ? `${min} – ${max}` : "Enter a number"}
        />
        {option.description && (
          <Text className="!text-[11px] block mt-1" style={{ color: "#9ca3af" }}>
            {option.description}
          </Text>
        )}
      </div>
    );
  }

  //  Single-select (string with fixed choices)
  if ((option.values?.length ?? 0) > 0) {
    return (
      <div>
        {labelNode}
        <Select
          value={typeof value === "string" ? value : undefined}
          onChange={(val) => onChange(val as string)}
          options={option.values!.map((v) => ({ value: v.value, label: v.label }))}
          className="w-full"
          size="small"
          placeholder="Select…"
          allowClear={!option.default}
        />
      </div>
    );
  }

  // string with no enumerated values and not a special type → not renderable
  return null;
}

function ChartOptionsSection({
  chart,
  state,
  downloadResult,
  onOptionChange,
}: {
  chart: AprChart;
  state: ChartOptionState;
  downloadResult?: ChartDownloadResult;
  onOptionChange: (name: string, value: AprOptionValue) => void;
}) {
  const meta = CHART_TYPE_META[chart.chartType];

  // Only keep options that can actually be rendered
  const renderableOptions = chart.options.filter(
    (opt) =>
      opt.type === "number" || opt.type === "string[]" || (opt.type === "string" && (opt.values?.length ?? 0) > 0),
  );

  const statusIcon =
    downloadResult?.status === "pending" ? (
      <LoadingOutlined style={{ color: ACCENT }} />
    ) : downloadResult?.status === "success" ? (
      <CheckCircleOutlined style={{ color: "#16a34a" }} />
    ) : downloadResult?.status === "error" ? (
      <Tooltip title={downloadResult.error}>
        <CloseCircleOutlined style={{ color: "#dc2626" }} />
      </Tooltip>
    ) : null;

  return (
    <div className="px-5 py-4 border-b border-gray-100 last:border-b-0">
      {/* Section header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className="text-base leading-none" style={{ color: meta.color }}>
            {meta.icon}
          </span>
          <Text className="!text-[14px] !font-semibold !text-[#111827]">{chart.title}</Text>
        </div>
        <div className="flex items-center gap-2">
          <ChartTypeBadge type={chart.chartType} />
          {statusIcon && <span className="text-sm">{statusIcon}</span>}
        </div>
      </div>

      {/* Options grid */}
      {renderableOptions.length > 0 ? (
        <div className="grid gap-x-4 gap-y-3" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(195px, 1fr))" }}>
          {renderableOptions.map((opt) => (
            <OptionControl
              key={opt.name}
              option={opt}
              value={state[opt.name]}
              siblingState={state}
              onChange={(val) => onOptionChange(opt.name, val)}
            />
          ))}
        </div>
      ) : (
        <Text className="!text-[12px] italic" style={{ color: "#9ca3af" }}>
          No configuration required — ready to download.
        </Text>
      )}
    </div>
  );
}

function DownloadProgressPanel({ results }: { results: ChartDownloadResult[] }) {
  return (
    <div className="p-5 flex flex-col gap-2">
      {results.map((r) => (
        <div
          key={r.chartKey}
          className="flex items-center gap-3 px-3 py-2.5 rounded-lg border border-gray-100 bg-gray-50"
        >
          <span className="flex-shrink-0 text-sm leading-none">
            {r.status === "pending" && <LoadingOutlined style={{ color: ACCENT }} />}
            {r.status === "success" && <CheckCircleOutlined style={{ color: "#16a34a" }} />}
            {r.status === "error" && <CloseCircleOutlined style={{ color: "#dc2626" }} />}
          </span>

          <div className="flex-1 min-w-0">
            <Text className="!text-[13px] !font-medium !text-[#111827] block">{r.title}</Text>
            {r.status === "error" && r.error && (
              <Text className="!text-[11px] block" style={{ color: "#dc2626" }}>
                {r.error}
              </Text>
            )}
          </div>

          <Text className="!text-[11px] capitalize flex-shrink-0" style={{ color: "#6b7280" }}>
            {r.status === "pending" ? "Downloading…" : r.status}
          </Text>
        </div>
      ))}
    </div>
  );
}

export default function ChartDownloadModal({
  open,
  onClose,
  charts,
  applicationId,

  onAttach,
}: ChartDownloadModalProps) {
  const isAttachMode = typeof onAttach === "function";
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [optState, setOptState] = useState<AllOptionsState>({});
  const [isDownloading, setIsDl] = useState(false);
  const [dlResults, setDlResults] = useState<ChartDownloadResult[] | null>(null);

  // ── Derived state ──────────────────────────────────────────────────────────

  const availableCharts = useMemo(() => charts.filter((c) => c.available), [charts]);

  /** Intersection of selected keys and available charts (in original order) */
  const selectedAvailable = useMemo(() => charts.filter((c) => selected.has(c.key) && c.available), [charts, selected]);

  const allAvailableSelected = availableCharts.length > 0 && availableCharts.every((c) => selected.has(c.key));

  /** True once downloading finishes (results exist + not currently running) */
  const showResults = dlResults !== null && !isDownloading;

  const completedCount = dlResults?.filter((r) => r.status !== "pending").length ?? 0;
  const successCount = dlResults?.filter((r) => r.status === "success").length ?? 0;
  const failCount = dlResults?.filter((r) => r.status === "error").length ?? 0;

  const handleToggleChart = useCallback(
    (key: string, checked: boolean) => {
      const chart = charts.find((c) => c.key === key);
      if (!chart?.available) return;

      setSelected((prev) => {
        const next = new Set(prev);
        checked ? next.add(key) : next.delete(key);
        return next;
      });

      // Initialise defaults only on first selection
      if (checked) {
        setOptState((prev) => ({
          ...prev,
          [key]: prev[key] ?? defaultOptionsFor(chart),
        }));
      }
    },
    [charts],
  );

  const handleToggleAll = useCallback(() => {
    if (allAvailableSelected) {
      setSelected(new Set());
    } else {
      setSelected(new Set(availableCharts.map((c) => c.key)));
      setOptState((prev) => {
        const next = { ...prev };
        for (const chart of availableCharts) {
          if (!next[chart.key]) next[chart.key] = defaultOptionsFor(chart);
        }
        return next;
      });
    }
  }, [allAvailableSelected, availableCharts]);

  const handleOptionChange = useCallback(
    (chartKey: string, optName: string, value: AprOptionValue, allOpts: AprChartOption[]) => {
      setOptState((prev) => {
        const next = { ...prev[chartKey], [optName]: value };
        // Clear options that are now conditionally hidden by this change
        for (const opt of allOpts) {
          if (!isOptionVisible(opt, next)) delete next[opt.name];
        }
        return { ...prev, [chartKey]: next };
      });
    },
    [],
  );

  const handleDownload = useCallback(async () => {
    if (!selectedAvailable.length) return;
    setIsDl(true);

    const results: ChartDownloadResult[] = selectedAvailable.map((c) => ({
      chartKey: c.key,
      title: c.title,
      status: "pending" as const,
    }));
    setDlResults([...results]);

    // slots that will hold a File on success (null on error)
    const collectedFiles: (File | null)[] = selectedAvailable.map(() => null);

    await Promise.allSettled(
      selectedAvailable.map(async (chart, i) => {
        try {
          const qs = buildQueryString(chart, optState[chart.key] ?? {});
          // const url = `/risk-plus/application/${applicationId}/apr-summary/chart/${chart.key}${qs}`;
          // const res = await api.get(url, {});
          // if (!res.ok) throw new Error(`HTTP ${res.status}`);

          // const blob = await res.blob();
          const url = `/risk-plus/application/${applicationId}/apr-summary/chart/${chart.key}${qs}`;

          const res = await api.get(url, { responseType: "blob" });
          const blob: Blob = res.data;

          if (isAttachMode) {
            // ── Attach mode: store as File, parent adds to attachedFiles ──────
            collectedFiles[i] = new File([blob], `${chart.key}.png`, { type: "image/png" });
          } else {
            // ── Download mode: trigger native browser save ────────────────────
            const href = URL.createObjectURL(blob);
            const a = Object.assign(document.createElement("a"), { href, download: `${chart.key}.png` });
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(href);
          }

          results[i] = { ...results[i], status: "success" };
        } catch (err) {
          results[i] = {
            ...results[i],
            status: "error",
            error: err instanceof Error ? err.message : "Failed",
          };
        }
        setDlResults([...results]);
      }),
    );

    setIsDl(false);

    // Pass successful files up — parent decides whether to close the modal
    if (isAttachMode) {
      const successfulFiles = collectedFiles.filter((f): f is File => f !== null);
      onAttach!(successfulFiles, selectedAvailable.length); // ← pass total
    }
  }, [selectedAvailable, optState, applicationId, isAttachMode, onAttach]);
  const handleClose = useCallback(() => {
    if (isDownloading) return;
    setSelected(new Set());
    setOptState({});
    setDlResults(null);
    onClose();
  }, [isDownloading, onClose]);
  useEffect(() => {
    if (isDownloading) return;
    if (dlResults?.length || showResults) {
      setSelected(new Set());
      setOptState({});
      setDlResults(null);
    }
  }, [showResults]);

  return (
    <Modal
      open={open}
      centered
      title={isAttachMode ? "Attach APR Charts" : "Download APR Charts"}
      onCancel={handleClose}
      width={1040}
      footer={null}
      closable={!isDownloading}
      maskClosable={!isDownloading}
      destroyOnHidden
      styles={{ body: { padding: 0 } }}
    >
      <div className="flex flex-col">
        {/* ── Two-column body ──────────────────────────────────────────────── */}
        <div className="flex" style={{ height: 528 }}>
          {/* ── LEFT PANEL: chart picker ───────────────────────────────────── */}
          <div
            className="w-[300px] flex-shrink-0 flex flex-col"
            style={{ background: DARK_BG, borderRight: `1px solid ${DARK_BORDER}` }}
          >
            {/* Header */}
            <div className="px-3.5 py-4 flex-shrink-0" style={{ borderBottom: `1px solid ${DARK_BORDER}` }}>
              <Text className="!text-[13px] !font-semibold block" style={{ color: DARK_TEXT }}>
                Select Charts
              </Text>
              <Text className="!text-[12px] block" style={{ color: DARK_MUTED }}>
                {selected.size > 0
                  ? `${selected.size} selected`
                  : isAttachMode
                    ? "Choose charts to attach"
                    : "Choose charts to download"}
              </Text>
            </div>

            {/* Scrollable chart list */}
            <div className="flex-1 overflow-y-auto">
              {charts.map((chart) => (
                <ChartListItem
                  key={chart.key}
                  chart={chart}
                  selected={selected.has(chart.key)}
                  onToggle={(checked) => handleToggleChart(chart.key, checked)}
                />
              ))}
            </div>

            {/* Select / deselect all */}
            <div className="px-3.5 py-2.5 flex-shrink-0" style={{ borderTop: `1px solid ${DARK_BORDER}` }}>
              <Button
                type="link"
                size="small"
                className="!p-0 !h-auto !text-[12px]"
                style={{ color: ACCENT }}
                onClick={handleToggleAll}
                disabled={availableCharts.length === 0}
              >
                {allAvailableSelected ? "Deselect all" : "Select all available"}
              </Button>
            </div>
          </div>

          {/* ── RIGHT PANEL: options configuration ────────────────────────── */}
          <div className="flex-1 flex flex-col min-w-0 bg-white">
            {/* Panel header */}
            <div className="px-5 py-4 border-b border-gray-100 flex-shrink-0">
              <Text className="!text-[13px] !font-semibold !text-[#111827] block">
                {showResults ? "Download Results" : "Configure Options"}
              </Text>
              <Text className="!text-[12px] block" style={{ color: "#9ca3af" }}>
                {isDownloading
                  ? `Downloading ${completedCount} of ${selectedAvailable.length}…`
                  : showResults
                    ? `${successCount} succeeded · ${failCount} failed`
                    : selected.size === 0
                      ? "Pick charts from the left panel to configure"
                      : `${selectedAvailable.length} chart${selectedAvailable.length !== 1 ? "s" : ""} ready to configure`}
              </Text>
            </div>

            {/* Scrollable content */}
            <div className="flex-1 overflow-y-auto">
              {isDownloading || showResults ? (
                <DownloadProgressPanel results={dlResults ?? []} />
              ) : selected.size === 0 ? (
                <div className="h-full flex items-center justify-center">
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description={
                      <Text className="!text-[13px]" style={{ color: "#9ca3af" }}>
                        Select charts from the left to configure options
                      </Text>
                    }
                  />
                </div>
              ) : (
                // ── Per-chart option sections ────────────────────────────────
                selectedAvailable.map((chart) => (
                  <ChartOptionsSection
                    key={chart.key}
                    chart={chart}
                    state={optState[chart.key] ?? {}}
                    downloadResult={dlResults?.find((r) => r.chartKey === chart.key)}
                    onOptionChange={(name, val) => handleOptionChange(chart.key, name, val, chart.options)}
                  />
                ))
              )}
            </div>
          </div>
        </div>

        <div className="flex items-center justify-between px-5 py-3 bg-gray-50 border-t border-gray-100">
          <Text className="!text-[13px]" style={{ color: "#6b7280" }}>
            {showResults
              ? `${successCount} of ${dlResults!.length} charts downloaded`
              : selected.size === 0
                ? "No charts selected"
                : `${selectedAvailable.length} chart${selectedAvailable.length !== 1 ? "s" : ""} selected`}
          </Text>

          <Space>
            {showResults ? (
              <Button onClick={handleClose}>Close</Button>
            ) : (
              <>
                <Button onClick={handleClose} disabled={isDownloading}>
                  Cancel
                </Button>
                <Button
                  type="primary"
                  icon={!isDownloading ? isAttachMode ? <PaperClipOutlined /> : <DownloadOutlined /> : undefined}
                  loading={isDownloading}
                  disabled={selectedAvailable.length === 0}
                  onClick={handleDownload}
                  style={selectedAvailable.length > 0 ? { background: ACCENT, borderColor: ACCENT } : undefined}
                >
                  {isDownloading
                    ? isAttachMode
                      ? "Attaching..."
                      : "Downloading…"
                    : `${isAttachMode ? "Attach" : `Download`}${
                        selectedAvailable.length > 0
                          ? ` ${selectedAvailable.length} Chart${selectedAvailable.length !== 1 ? "s" : ""}`
                          : ""
                      }`}
                </Button>
              </>
            )}
          </Space>
        </div>
      </div>
    </Modal>
  );
}
